import { Component, Ref } from 'vue-property-decorator'
import ConfigurationSectionHeader from '@/modules/configurationModule/ConfigurationSectionHeader.vue'
import VtGrid from '@/components/VtGrid/VtGrid.vue'
import { GetContextMenuItemsParams, MenuItemDef, RowClickedEvent, RowNode } from '@ag-grid-enterprise/all-modules'
import InsurersModalBasicForm from '@/modules/configurationModule/detail/cases/insurers/components/modal-basic-form/insurers-modal-basic-form.vue'
import ConfigurationSidepanel from '@/modules/configurationModule/detail/ConfigurationSidepanel.vue'
import { BaseCrud } from '@/shared/models/cruds.models'
import BaseCrudServerSideComponent from '@/shared/resources/base-crud-server-side.component'
import { initialInsurersGridState, InsurersGridState } from '@/modules/configurationModule/detail/cases/insurers/components/grid/insurers-grid.model'
import ModalWrapper from '@/components/ModalWrapper.vue'
import { ConfigurationInsurerCreateRequest, ConfigurationInsurerPatchInBatchRequest, ConfigurationInsurerResponse } from '@/modules/configurationModule/detail/cases/insurers/services/configuration-insurer.dto'
import { configurationInsurerService } from '@/modules/configurationModule/detail/cases/insurers/services/configuration-insurer.service'
import { StrictNullable } from '@/shared/types/app.types'
import ConfigurationListSidepanel from '@/modules/configurationModule/shared/components/configuration-list-sidepanel/configuration-list-sidepanel.vue'
import ConfigurationListSidepanelComp from '@/modules/configurationModule/shared/components/configuration-list-sidepanel/configuration-list-sidepanel'

@Component({
    components: {
        ConfigurationSectionHeader,
        VtGrid,
        InsurersModalBasicForm,
        ConfigurationSidepanel,
        ConfigurationListSidepanel
    }
})
export default class InsurersGrid extends BaseCrudServerSideComponent<any> implements BaseCrud {
    @Ref() readonly insurersModalBasicFormWrapper!: typeof ModalWrapper
    @Ref('configurationListSidepanel') readonly configurationListSidepanel!: ConfigurationListSidepanelComp

    state: InsurersGridState
    constructor() {
        super()
        this.state = initialInsurersGridState()
    }

    get cantUpdate(): boolean {
        return !this.canUpdate
    }

    get canUpdate(): boolean {
        return this.state.gridApi?.getSelectedRows().length === 1
    }

    get cantDelete(): boolean {
        return !this.canDelete
    }

    get canDelete(): boolean {
        return !!this.state.gridApi?.getSelectedRows().length
    }

    get canActivateBatch():boolean {
        return this.hasDeactiveInsurer && !this.hasAllActivated
    }

    get hasDeactiveInsurer():boolean {
        return !!this.state.selectedRows.find(insurer => !insurer.active)
    }

    get canDeactivateBatch():boolean {
        return this.state.selectedRows.length > 0 && this.hasActivePriority && !this.hasAllDeactivated
    }

    get hasActivePriority():boolean {
        return !!this.state.selectedRows.find(insurer => insurer.active)
    }

    getContextMenuItems(params: GetContextMenuItemsParams): MenuItemDef[] {
        if (!params?.node) {
            return []
        } else {
            return [
                {
                    name: this.$tc('configuration.actions.update'),
                    cssClasses: this.state.selectedRows.length > 1 ? ['not-show'] : [''],
                    action: () => {
                        const nodeSelected: StrictNullable<RowNode> = params?.node
                        const selectedRow: ConfigurationInsurerResponse = nodeSelected?.data
                        this.onUpdate()
                    }
                },
                {
                    name: this.$tc('configuration.actions.delete'),
                    cssClasses: this.state.selectedRows.length < 1 ? ['not-show'] : [''],
                    action: () => this.deleteOperation(this.state.selectedRows)
                },
                {
                    name: this.$tc('configuration.actions.activate'),
                    cssClasses: this.canActivateBatch ? [''] : ['not-show'],
                    action: () => {
                        this.activateDeactivateSelection(true, this.state.selectedRows)
                    }
                },
                {
                    name: this.$tc('configuration.actions.deactivate'),
                    cssClasses: this.canDeactivateBatch ? [''] : ['not-show'],
                    action: () => {
                        this.activateDeactivateSelection(false, this.state.selectedRows)
                    }
                }
            ]
        }
    }

    async activateDeactivateSelection(option: boolean, selection: ConfigurationInsurerResponse[]): Promise<void> {
        const selectionToModify = selection.filter(selected => selected.active !== option).map(selected => {
            selected.active = option
            return selected
        })
        const result = await this.$_toCallSecureLoading(configurationInsurerService.patch(selectionToModify))
        if (result) {
            this.$_showToastSuccess(this.$tc('configuration.toastMessages.updated'))
            this.reloadData()
        }
    }

    onAdd(): void {
        this.showModal(this.$tc('configuration.actions.add'), null, this.insurersModalBasicFormWrapper)
    }

    onUpdate(): void {
        this.showModal(this.$tc('configuration.actions.update'), this.state.selectedRows[0], this.insurersModalBasicFormWrapper)
    }

    async createOperation(row: ConfigurationInsurerCreateRequest): Promise<void> {
        const result = await this.$_toCallSecureLoading(configurationInsurerService.create(row))
        if (result) {
            this.$_showToastSuccess(this.$tc('configuration.toastMessages.created'))
            this.reloadData()
        }
    }

    async patchOperation(row: ConfigurationInsurerPatchInBatchRequest): Promise<void> {
        const result = await this.$_toCallSecureLoading(configurationInsurerService.patch([row]))
        if (result) {
            this.$_showToastSuccess(this.$tc('configuration.toastMessages.updated'))
            this.reloadData()
        }
    }

    async deleteselectedInsurers(): Promise<void> {
        this.deleteOperation(this.state.selectedRows)
    }

    onRowClicked(event: RowClickedEvent): void {
        this.state.sidepanelTitle = event.node.data.insurer
        /* TODO remove this and implement the real sidepanel
        this.configurationListSidepanel.show({
            entities: [
                {
                    id: 1,
                    titleKey: 'Listado de Entidades 1',
                    entityKey: 'Entidades 1'
                },
                {
                    id: 2,
                    titleKey: 'Listado de Entidades 2',
                    entityKey: 'Entidades 2'
                }
            ],
            obtainEntityList: async (entity: ConfigurationListSidePanelEntity): Promise<string[]> => {
                function sleep(ms:number):Promise<number> {
                    return new Promise(resolve => setTimeout(resolve, ms))
                }
                await sleep(2000)
                if (entity.id === 1) {
                    return Promise.resolve(['valor1', 'valor2', 'valor3'])
                } else {
                    return Promise.resolve(['valor4', 'valor5', 'valor6', 'valor7'])
                }
            }
        })
        */
    }

    async deleteOperation(insurers: ConfigurationInsurerResponse[]): Promise<void> {
        if (insurers.length) {
            const selectedRowsSize: number = this.state?.selectedRows?.length
            const labelConfirmation: string = this.$tc('configuration.grid.deleteConfirmation')
            const labelGrid: string = this.$tc('configuration.grid.element', selectedRowsSize)
            const labelDeleteQuestion: string = this.$tc('configuration.grid.deleteQuestion')
            const labelDeleteAction: string = this.$tc('configuration.actions.delete')
            const deleteConfirmation = await this.$_showConfirmationModal(
                `${labelConfirmation} ${selectedRowsSize} ${labelGrid}, <br/> ${labelDeleteQuestion}`,
                labelDeleteAction
            )

            if (deleteConfirmation) {
                const toRemove = {
                    entityIdentifiers: insurers.map(insurer => insurer.id)
                }
                const result = await this.$_toCallSecureLoading(configurationInsurerService.delete(toRemove))
                if (result) {
                    this.$_showToastSuccess(this.$tc('configuration.toastMessages.deleted'))
                    this.reloadData()
                }
            }
        }
    }
}
